/**
 * Rybbit event payload helpers for LiveFlight.
 *
 * Rybbit's /api/track endpoint enforces a 2048 character limit on the
 * `properties` field. These helpers produce slim payloads that stay
 * well under the limit while keeping every field that matters for analytics.
 */

/**
 * Slim a full seat object down to analytics-relevant fields.
 *
 * Drops: travelerPricing array, coordinates, left/right neighbors,
 *        isPartiallyAvailable, currentTravelerChoosing, etc.
 */
export const slim_seat = (seat) => {
  if (!seat) {
    return;
  }
  const { travelerPricing = [] } = seat;
  if (travelerPricing.length === 0) {
    return;
  }
  const { price: pricing = {} } = travelerPricing[0];
  return {
    number: seat.number,
    price: pricing.grandTotal || pricing.total,
  };
};

/**
 * Slim selections map from { segmentId: { travelerId: fullSeatObject } }
 * to { segmentId: { travelerId: seatNumber } }.
 */
export const slim_selections = (selections) => {
  if (!selections) {
    return {};
  }
  return Object.keys(selections).reduce((acc, seg_id) => {
    acc[seg_id] = Object.keys(selections[seg_id] || {}).reduce(
      (seg_acc, t_id) => {
        const { number: seat } = selections[seg_id][t_id] || {};
        seg_acc[t_id] = seat;
        return seg_acc;
      },
      {},
    );
    return acc;
  }, {});
};

/**
 * Slim an automated check-in offer — `{ available, price }` as the probe
 * resolves it — to the only three fields any of its events carry.
 */
export const slim_automated_checkin_offer = (offer) => {
  const { available = false, price, reason_code = '' } = offer || {};
  const { grandTotal = '', total = '' } = price || {};
  return {
    type: 'info',
    available: available ? 1 : 0,
    price: Number(grandTotal || total) || 0,
    reason_code,
  };
};

/**
 * Slim a traveler object to analytics-safe fields (no PII like birth/gender).
 */
export const slim_traveler = (traveler) => {
  if (!traveler) return undefined;
  return {
    id: traveler.id,
    traveler_type: traveler.traveler_type || traveler.travelerType,
  };
};

/**
 * Extract a slim flight-offer summary for analytics.
 *
 * Previously lived in flight-offer.js as get_flight_offer_rybbit_data.
 */
export const slim_flight_offer = (flight_offer) => {
  if (!flight_offer) return {};

  const itineraries = (flight_offer.itineraries || []).map((itinerary) => {
    const segments = itinerary.segments || [];
    const first_segment = segments[0];
    const last_segment = segments[segments.length - 1];
    const { carrier: first_carrier = {} } = first_segment || {};
    const { departure: first_departure = {} } = first_segment || {};
    const { arrival: last_arrival = {} } = last_segment || {};

    return {
      departure_airport: first_departure.iataCode,
      arrival_airport: last_arrival.iataCode,
      departure_date: first_departure.at,
      arrival_date: last_arrival.at,
      stops: segments.length - 1,
      carrier: first_carrier.code,
    };
  });

  const traveler_pricings = flight_offer.travelerPricings || [];
  const { fareDetailsBySegment: first_fare_details = [] } = traveler_pricings[0] || {};
  const { brandedFare: branded_fare = "" } = first_fare_details[0] || {};

  return {
    source: flight_offer.source || "",
    branded_fare,
    itineraries,
  };
};
